import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useFetch from "hooks/useFetch";
import {
  Alert,
  Badge,
  BadgeTone,
  Button,
  Card,
  EmptyState,
  Icon,
  Select,
  PageHeader,
  Skeleton,
  styles as ui,
  TextField,
  Toggle,
  useConfirm,
  useToast,
} from "stories/Admin";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import styles from "./JanazahPage.module.scss";
import JanazahUpload, { usePushConfirm } from "./JanazahUpload";
import usePendingActions from "./usePendingActions";

type CeremonyType = "JANAZAH" | "BURIAL" | "DUA" | "OTHER";

interface DeathNoticeCeremony {
  type: CeremonyType;
  label: string;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  place: string | null;
  address: string | null;
  note: string | null;
}

interface DeathNoticeRelative {
  name: string;
  relation: string | null;
  phone: string | null;
}

interface DeathNotice {
  id: number;
  source: string;
  sourceName: string;
  sourceUrl: string;
  name: string;
  imageUrl: string | null;
  birthDate: string | null;
  deathDate: string | null;
  janazahDate: string | null;
  janazahTime: string | null;
  janazahPlace: string | null;
  janazahAddress: string | null;
  city: string | null;
  ceremonies: DeathNoticeCeremony[];
  obituary: string | null;
  funeralInfo: string | null;
  relatives: DeathNoticeRelative[];
  firstSeenAt: string;
  updatedAt: string;
  /** Lastet opp som bilde og ennå ikke funnet hos byrået. */
  manual?: boolean;
  /** Bildet av dødsannonsen, når den ble lastet opp. */
  noticeImageUrl?: string | null;
  /** PUBLISHED, eller PENDING_REVIEW for opplastinger fra appene som venter på godkjenning. */
  status?: "PUBLISHED" | "PENDING_REVIEW";
  agencyKey?: string | null;
  /** Byråets navn (fra byrålista, ellers slik det står på plakaten). */
  agencyName?: string | null;
  agencyLogoUrl?: string | null;
  /** Når den ble lastet opp fra appen. */
  submittedAt?: string | null;
}

interface DeathNoticeSyncReport {
  source: string;
  linksFound: number;
  created: number;
  updated: number;
  failed: number;
  errors: string[];
  skipped?: number;
  /** Annonser byrået har slettet (siden svarer 404), fjernet ved synk. */
  removed?: number;
}

interface DeathNoticeAdmin {
  notices: DeathNotice[];
  lastSyncAt: string | null;
  lastSyncReports: DeathNoticeSyncReport[];
  cronSchedule: string;
  /** Opplastinger fra appene som venter på godkjenning, nyeste først. */
  pending?: DeathNotice[];
}

const formatDate = (value: string | null): string => {
  if (!value) return "—";
  const [y, m, d] = value.split("-");
  return d && m && y ? `${d}.${m}.${y}` : value;
};

const formatTime = (value: string | null): string =>
  value ? value.slice(0, 5) : "";

const formatDateTime = (value: string | null): string => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("nb-NO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

/** Janazah eller en annen seremoni (f.eks. dua) i dag eller senere. */
const isUpcoming = (n: DeathNotice, today: string): boolean =>
  (n.janazahDate ?? "") >= today ||
  n.ceremonies.some((c) => (c.date ?? "") >= today);

const todayIso = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const ceremonyTone: Record<CeremonyType, BadgeTone> = {
  JANAZAH: "mint",
  BURIAL: "grey",
  DUA: "blue",
  OTHER: "lilac",
};

const JanazahPage: React.FC = () => {
  const { execute } = useFetch();
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const [data, setData] = useState<DeathNoticeAdmin | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncReports, setSyncReports] = useState<
    DeathNoticeSyncReport[] | null
  >(null);
  const [source, setSource] = useState<string>("ALL");
  const [search, setSearch] = useState("");
  const [onlyUpcoming, setOnlyUpcoming] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [showUpload, setShowUpload] = useState(false);
  /** Annonsen som oppdateres fra et nytt bilde (null: ny annonse). */
  const [uploadTarget, setUploadTarget] = useState<DeathNotice | null>(null);
  const confirmPush = usePushConfirm();
  const openUpload = (target: DeathNotice | null) => {
    setUploadTarget(target);
    setShowUpload(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const confirm = useConfirm();
  const toast = useToast();
  const refreshRef = useRef<() => void>(() => {});
  const pendingActions = usePendingActions(() => refreshRef.current());

  const handleDelete = async (n: DeathNotice) => {
    const ok = await confirm({
      title: `Slette «${n.name}»?`,
      message: "Den opplastede dødsannonsen fjernes fra appen.",
      confirmLabel: "Slett",
      tone: "danger",
    });
    if (!ok) return;
    // 204 gir tom kropp (og null fra execute), så sjekk lista etterpå.
    await executeRef.current(`/api/v1/death-notices/admin/${n.id}`, "DELETE");
    const result = (await executeRef.current(
      "/api/v1/death-notices/admin",
      "GET",
    )) as DeathNoticeAdmin | null;
    if (result) setData(result);
    if (result?.notices.some((x) => x.id === n.id)) {
      toast.error(`Kunne ikke slette ${n.name}.`);
    } else {
      toast.success(`${n.name} er slettet.`);
    }
  };

  const toggleExpanded = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const fetchData = useCallback(async (showLoading = true) => {
    if (showLoading) setIsLoading(true);
    const result = (await executeRef.current(
      "/api/v1/death-notices/admin",
      "GET",
    )) as DeathNoticeAdmin | null;
    if (result) setData(result);
    if (showLoading) setIsLoading(false);
  }, []);

  refreshRef.current = () => fetchData(false);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [isMatching, setIsMatching] = useState(false);

  /** Kobler alle dødsannonsene til moskeen der janazah holdes på nytt (POST /admin/mosque-match). */
  const handleMosqueMatch = async () => {
    setIsMatching(true);
    try {
      const result = (await executeRef.current(
        "/api/v1/death-notices/admin/mosque-match?all=true",
        "POST",
      )) as { checked: number; matched: number } | null;
      if (!result) {
        toast.error("Kunne ikke koble moskeer på nytt.");
        return;
      }
      toast.success(
        `Sjekket ${result.checked} dødsannonser, ${result.matched} har janazah i en moské i iBønn.`,
      );
      await fetchData(false);
    } finally {
      setIsMatching(false);
    }
  };

  const handleSync = async (full = false) => {
    setIsSyncing(true);
    try {
      const reports = (await executeRef.current(
        `/api/v1/death-notices/sync?full=${full}`,
        "POST",
      )) as DeathNoticeSyncReport[] | null;
      if (reports) setSyncReports(reports);
      await fetchData(false);
    } finally {
      setIsSyncing(false);
    }
  };

  const notices = useMemo(() => {
    const today = todayIso();
    const q = search.trim().toLowerCase();
    return (data?.notices ?? []).filter((n) => {
      if (source !== "ALL" && n.source !== source) return false;
      if (onlyUpcoming && !isUpcoming(n, today)) return false;
      if (!q) return true;
      return [n.name, n.janazahPlace, n.janazahAddress, n.city]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(q));
    });
  }, [data, source, search, onlyUpcoming]);

  const counts = useMemo(() => {
    const all = data?.notices ?? [];
    const today = todayIso();
    return {
      total: all.length,
      pending: data?.pending?.length ?? 0,
      uploaded: all.filter((n) => !!n.submittedAt).length,
      upcoming: all.filter((n) => (n.janazahDate ?? "") >= today).length,
    };
  }, [data]);

  /** Kildene som finnes i lista (byråene og «Delt i iBønn»). */
  const sources = useMemo(() => {
    const map = new Map<string, string>();
    (data?.notices ?? []).forEach((n) =>
      map.set(n.source, n.agencyName || n.sourceName || n.source),
    );
    return Array.from(map.entries()).sort((a, b) =>
      a[1].localeCompare(b[1], "nb"),
    );
  }, [data]);

  const reports = syncReports ?? data?.lastSyncReports ?? [];
  const busy = isLoading || isSyncing;

  return (
    <>
      <PageHeader
        title="Janazah"
        subtitle={
          <>
            Dødsannonser fra byråene (muslimsk.no og alkhidmat.no synkes) og
            plakater delt fra appene. Siste synk:{" "}
            {formatDateTime(data?.lastSyncAt ?? null)}
            {data?.cronSchedule && (
              <span className={styles.cron}> (cron {data.cronSchedule})</span>
            )}
          </>
        }
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => fetchData()}
              disabled={busy}
              icon={
                isLoading ? (
                  <span className={ui.spinner} aria-hidden="true" />
                ) : (
                  <Icon name="swap" size={16} strokeWidth={2} />
                )
              }
            >
              Oppdater
            </Button>
            <Button
              variant="secondary"
              onClick={() => openUpload(null)}
              disabled={busy || showUpload}
              title="Last opp et bilde av en dødsannonse, f.eks. fra Al-Khidmat på Facebook"
              icon={<Icon name="upload" size={16} strokeWidth={2} />}
            >
              Last opp dødsannonse
            </Button>
            <Button
              variant="secondary"
              onClick={handleMosqueMatch}
              disabled={busy || isMatching}
              loading={isMatching}
              title="Finner moskeen i iBønn der janazah holdes for alle dødsannonsene på nytt"
              icon={<Icon name="mosque" size={16} strokeWidth={2} />}
            >
              Koble moskeer på nytt
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleSync(true)}
              disabled={busy}
              title="Henter alle annonsene på nytt, også de som er passert"
            >
              Full synk
            </Button>
            <Button
              onClick={() => handleSync(false)}
              disabled={busy}
              loading={isSyncing}
              title="Henter nye og kommende annonser"
              icon={<Icon name="download" size={16} strokeWidth={2} />}
            >
              {isSyncing ? "Synkroniserer …" : "Synk nå"}
            </Button>
          </>
        }
      />

      <SectionTabs />

      {(data?.pending?.length ?? 0) > 0 && (
        <Card title={`Venter på godkjenning (${data?.pending?.length ?? 0})`}>
          <p className={ui.help}>
            Lastet opp fra appene, men ikke publisert: AI-en var usikker eller
            noe manglet. De vises ikke i appen før de er godkjent.
          </p>
          <div className={styles.pendingList}>
            {data?.pending?.map((n) => {
              const busy = pendingActions.busyId === n.id;
              return (
                <article key={n.id} className={styles.pendingItem}>
                  {n.noticeImageUrl ? (
                    <a
                      href={n.noticeImageUrl}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.pendingImageLink}
                    >
                      <img
                        src={n.noticeImageUrl}
                        alt={`Plakaten for ${n.name}`}
                        className={styles.pendingImage}
                      />
                    </a>
                  ) : (
                    <span className={styles.pendingImage} />
                  )}
                  <div className={styles.pendingBody}>
                    <b>{n.name}</b>
                    <span className={ui.help}>
                      {n.birthDate || n.deathDate
                        ? `${n.birthDate ? formatDate(n.birthDate) : ""}${
                            n.birthDate || n.deathDate ? " – " : ""
                          }${n.deathDate ? formatDate(n.deathDate) : ""}`
                        : "Ingen datoer"}
                    </span>
                    <span>
                      Janazah:{" "}
                      {n.janazahDate
                        ? `${formatDate(n.janazahDate)}${
                            n.janazahTime
                              ? ` kl. ${formatTime(n.janazahTime)}`
                              : ""
                          }`
                        : "—"}
                      {(n.janazahPlace || n.janazahAddress) &&
                        ` · ${[n.janazahPlace, n.janazahAddress]
                          .filter(Boolean)
                          .join(", ")}`}
                    </span>
                    <span className={ui.help}>
                      {n.agencyName || n.sourceName}
                      {" · lastet opp "}
                      {formatDateTime(n.submittedAt ?? n.firstSeenAt)}
                    </span>
                    <div className={styles.pendingActions}>
                      <Button
                        size="sm"
                        disabled={busy}
                        loading={busy}
                        onClick={() =>
                          pendingActions.approve(n.id, n.name, true)
                        }
                        icon={<Icon name="bell" size={14} strokeWidth={2} />}
                      >
                        Godkjenn og send varsel
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busy}
                        onClick={() =>
                          pendingActions.approve(n.id, n.name, false)
                        }
                        icon={<Icon name="check" size={14} strokeWidth={2} />}
                      >
                        Godkjenn uten varsel
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={busy}
                        onClick={() => pendingActions.remove(n.id, n.name)}
                        icon={<Icon name="trash" size={14} strokeWidth={2} />}
                      >
                        Slett
                      </Button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </Card>
      )}

      {showUpload && (
        <JanazahUpload
          key={uploadTarget?.id ?? "new"}
          target={uploadTarget}
          onClose={() => setShowUpload(false)}
          onSaved={() => {
            setShowUpload(false);
            fetchData(false);
          }}
        />
      )}

      <div className={ui.g4}>
        {[
          [counts.total, "Publiserte annonser"],
          [counts.upcoming, "Kommende janazah"],
          [counts.pending, "Venter på godkjenning"],
          [counts.uploaded, "Delt fra appene"],
        ].map(([value, label]) => (
          <div key={label} className={styles.stat}>
            <b>{value}</b>
            <span>{label}</span>
          </div>
        ))}
      </div>

      {reports.length > 0 && (
        <Card title={syncReports ? "Resultat av synk" : "Siste synk"}>
          {reports.map((r) => (
            <div key={r.source} className={styles.report}>
              <span className={styles.reportLine}>
                <Badge
                  tone={r.failed > 0 || r.linksFound === 0 ? "gold" : "mint"}
                >
                  {r.source}
                </Badge>
                {r.linksFound} funnet, {r.created} nye, {r.updated} oppdatert,
                {r.skipped ? ` ${r.skipped} uendret (passert),` : ""}
                {r.removed ? ` ${r.removed} slettet hos byrået,` : ""}{" "}
                {r.failed} feilet
              </span>
              {r.errors.length > 0 && (
                <ul className={styles.errorList}>
                  {r.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </Card>
      )}

      <Card>
        <div className={styles.filters}>
          <Select
            aria-label="Kilde"
            fieldClassName={styles.filterSelect}
            value={source}
            onChange={(e) => setSource(e.target.value)}
          >
            <option value="ALL">Alle kilder</option>
            {sources.map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </Select>
          <div className={styles.search}>
            <Icon
              name="search"
              size={18}
              strokeWidth={2}
              className={styles.searchIcon}
            />
            <TextField
              type="search"
              aria-label="Søk på navn, sted eller by"
              placeholder="Søk på navn, sted eller by"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Toggle
            label="Bare kommende janazah"
            checked={onlyUpcoming}
            onChange={(e) => setOnlyUpcoming(e.target.checked)}
          />
        </div>

        {isLoading && !data ? (
          <>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height={44} />
            ))}
          </>
        ) : !data ? (
          <Alert tone="warn">
            Kunne ikke hente dødsannonser. Prøv å oppdatere.
          </Alert>
        ) : notices.length === 0 ? (
          <EmptyState icon="moon" title="Ingen dødsannonser">
            {data.notices.length > 0
              ? "Ingen dødsannonser passer filteret."
              : "Det er ingen dødsannonser ennå. Trykk «Synk nå»."}
          </EmptyState>
        ) : (
          <div className={`${ui.tblWrap} ${styles.tableWrapper}`}>
            <table className={ui.tbl}>
              <thead>
                <tr>
                  <th scope="col">
                    <span className={ui.srOnly}>Bilde</span>
                  </th>
                  <th scope="col">Navn</th>
                  <th scope="col">Født – død</th>
                  <th scope="col">Janazah</th>
                  <th scope="col">Sted</th>
                  <th scope="col">Seremonier</th>
                  <th scope="col">Kilde</th>
                </tr>
              </thead>
              <tbody>
                {notices.map((n) => {
                  // Alle rader kan åpnes: der ligger «Oppdater fra bilde» og «Send push».
                  const hasDetails = true;
                  const isOpen = expanded.has(n.id);
                  return (
                    <React.Fragment key={n.id}>
                      <tr
                        onClick={
                          hasDetails ? () => toggleExpanded(n.id) : undefined
                        }
                        className={`${hasDetails ? ui.rowClickable : ""} ${
                          isOpen ? ui.rowOn : ""
                        }`}
                        aria-expanded={hasDetails ? isOpen : undefined}
                      >
                        <td className={styles.nowrap}>
                          <span className={styles.avatarCell}>
                            <span className={styles.chevron}>
                              {hasDetails && (
                                <Icon
                                  name="chevronRight"
                                  size={14}
                                  strokeWidth={2}
                                  className={isOpen ? styles.chevronOpen : ""}
                                />
                              )}
                            </span>
                            {n.imageUrl ? (
                              <img
                                src={n.imageUrl}
                                alt=""
                                className={styles.avatar}
                              />
                            ) : (
                              <span className={styles.avatarPlaceholder}>
                                <Icon name="moon" size={16} />
                              </span>
                            )}
                          </span>
                        </td>
                        <td>
                          <b>{n.name}</b>
                          {n.manual && (
                            <>
                              {" "}
                              <Badge tone="gold">Opplastet</Badge>
                            </>
                          )}
                          {n.city && <div className={ui.help}>{n.city}</div>}
                        </td>
                        <td className={`${styles.nowrap} ${ui.num}`}>
                          {n.birthDate ? formatDate(n.birthDate) : ""}
                          {n.birthDate || n.deathDate ? " – " : ""}
                          {n.deathDate ? formatDate(n.deathDate) : ""}
                        </td>
                        <td className={`${styles.nowrap} ${ui.num}`}>
                          {n.janazahDate ? (
                            <span className={styles.janazahCell}>
                              <span>
                                {formatDate(n.janazahDate)}
                                {n.janazahTime && (
                                  <> kl. {formatTime(n.janazahTime)}</>
                                )}
                              </span>
                              {n.janazahDate >= todayIso() && (
                                <Badge tone="mint">Kommende</Badge>
                              )}
                            </span>
                          ) : (
                            <span className={styles.janazahCell}>
                              <span className={ui.help}>—</span>
                              {isUpcoming(n, todayIso()) && (
                                <Badge tone="mint">Kommende</Badge>
                              )}
                            </span>
                          )}
                        </td>
                        <td>
                          {n.janazahPlace || "—"}
                          {n.janazahAddress && (
                            <div className={ui.help}>{n.janazahAddress}</div>
                          )}
                        </td>
                        <td>
                          {n.ceremonies.length === 0 ? (
                            <span className={ui.help}>—</span>
                          ) : (
                            <ul className={styles.ceremonyList}>
                              {n.ceremonies.map((c, i) => (
                                <li key={i}>
                                  <Badge tone={ceremonyTone[c.type]}>
                                    {c.label}
                                  </Badge>{" "}
                                  {formatDate(c.date)}
                                  {c.startTime && ` ${formatTime(c.startTime)}`}
                                  {c.endTime && `–${formatTime(c.endTime)}`}
                                  {(c.place || c.address) && (
                                    <span className={ui.help}>
                                      {" "}
                                      ·{" "}
                                      {[c.place, c.address]
                                        .filter(Boolean)
                                        .join(", ")}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td>
                          <a
                            href={
                              n.sourceUrl.startsWith("http")
                                ? n.sourceUrl
                                : (n.noticeImageUrl ?? undefined)
                            }
                            target="_blank"
                            rel="noreferrer"
                            className={styles.link}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {n.agencyLogoUrl && (
                              <img
                                src={n.agencyLogoUrl}
                                alt=""
                                className={styles.agencyLogo}
                              />
                            )}
                            {n.agencyName || n.sourceName}
                            <Icon name="link" size={13} strokeWidth={2} />
                          </a>
                          {n.submittedAt && (
                            <div className={ui.help}>Delt fra appen</div>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className={styles.detailRow}>
                          <td></td>
                          <td colSpan={6}>
                            <div className={styles.rowActions}>
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={showUpload}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openUpload(n);
                                }}
                                icon={
                                  <Icon
                                    name="upload"
                                    size={14}
                                    strokeWidth={2}
                                  />
                                }
                              >
                                Oppdater fra bilde
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  confirmPush(n.id, n.name);
                                }}
                                icon={
                                  <Icon name="bell" size={14} strokeWidth={2} />
                                }
                              >
                                Send push
                              </Button>
                            </div>
                            <div className={styles.detailGrid}>
                              {n.ceremonies.some((c) => !!c.note) && (
                                <div>
                                  <h3 className={styles.detailTitle}>
                                    Seremonier
                                  </h3>
                                  {n.ceremonies
                                    .filter((c) => !!c.note)
                                    .map((c, i) => (
                                      <div key={i} className={styles.note}>
                                        <b>{c.label}</b>
                                        <div className={styles.preLine}>
                                          {c.note}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              )}
                              {n.funeralInfo && (
                                <div>
                                  <h3 className={styles.detailTitle}>
                                    Informasjon om gravferden
                                  </h3>
                                  <div className={styles.preLine}>
                                    {n.funeralInfo}
                                  </div>
                                </div>
                              )}
                              {(n.relatives?.length ?? 0) > 0 && (
                                <div>
                                  <h3 className={styles.detailTitle}>
                                    Pårørende
                                  </h3>
                                  <ul className={styles.relativeList}>
                                    {n.relatives.map((r, i) => (
                                      <li key={i}>
                                        {r.name}
                                        {r.relation && (
                                          <span className={ui.help}>
                                            {" "}
                                            ({r.relation})
                                          </span>
                                        )}
                                        {r.phone && (
                                          <a
                                            href={`tel:${r.phone.replace(/\s+/g, "")}`}
                                            className={styles.link}
                                          >
                                            {r.phone}
                                          </a>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {(n.noticeImageUrl || n.manual) && (
                                <div>
                                  <h3 className={styles.detailTitle}>
                                    Opplastet dødsannonse
                                  </h3>
                                  {n.noticeImageUrl && (
                                    <a
                                      href={n.noticeImageUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <img
                                        src={n.noticeImageUrl}
                                        alt={`Dødsannonsen for ${n.name}`}
                                        className={styles.noticeThumb}
                                      />
                                    </a>
                                  )}
                                  {n.manual ? (
                                    <>
                                      <p className={ui.help}>
                                        Slås sammen med byråets minneside når
                                        synken finner den.
                                      </p>
                                      <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDelete(n);
                                        }}
                                        icon={
                                          <Icon
                                            name="trash"
                                            size={14}
                                            strokeWidth={2}
                                          />
                                        }
                                      >
                                        Slett
                                      </Button>
                                    </>
                                  ) : (
                                    <p className={ui.help}>
                                      Slått sammen med byråets minneside.
                                    </p>
                                  )}
                                </div>
                              )}
                              {n.obituary && (
                                <div>
                                  <h3 className={styles.detailTitle}>
                                    Minnetekst
                                  </h3>
                                  <div className={styles.preLine}>
                                    {n.obituary}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
};

export default JanazahPage;
